import { useLang, buildWhatsAppUrl } from "../i18n";
import { useReveal } from "../hooks/useReveal";
import { MessageCircle, MapPin, Clock } from "lucide-react";

const WILLEMSTAD_IMG =
  "https://images.unsplash.com/photo-1693574276068-d5d65bb34ad0?fm=jpg&q=85&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0";
const ROTTERDAM_IMG =
  "https://images.unsplash.com/photo-1736846834517-cf36bf388efb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxyb3R0ZXJkYW0lMjBlcmFzbXVzYnJ1ZyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3Nzg2MTU2NTF8MA&ixlib=rb-4.1.0&q=85";

function StudioCard({ city, idx, accentVar, image, testidPrefix }) {
  const { t } = useLang();
  const r = useReveal();
  const data = idx === 0 ? t.studios.curacao : t.studios.rotterdam;
  const intent = `${t.whatsapp_pretext} (${data.name})`;

  return (
    <article
      ref={r}
      data-testid={`${testidPrefix}-card`}
      className="reveal relative group bg-neutral-950 border border-white/10 overflow-hidden"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={image}
          alt={`${data.name} architecture`}
          className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.04]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        <div className="absolute top-5 left-5 flex items-center gap-3">
          <span className="w-2.5 h-2.5" style={{ background: `hsl(var(${accentVar}))` }} />
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/70">
            0{idx + 1} / 02
          </span>
        </div>
        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-white/60">
              {data.country}
            </div>
            <h3 className="font-display text-4xl md:text-5xl uppercase tracking-tighter text-white mt-1">
              {data.name}
            </h3>
          </div>
        </div>
      </div>

      <div className="p-7 md:p-10">
        <p className="text-white/75 text-base md:text-lg leading-relaxed">{data.vibe}</p>

        <div className="mt-8 grid grid-cols-2 gap-4 text-xs uppercase tracking-[0.18em] text-white/55">
          <div className="flex items-start gap-2">
            <MapPin className="w-3.5 h-3.5 mt-0.5" style={{ color: `hsl(var(${accentVar}))` }} />
            <span>{data.name}, {data.country}</span>
          </div>
          <div className="flex items-start gap-2">
            <Clock className="w-3.5 h-3.5 mt-0.5" style={{ color: `hsl(var(${accentVar}))` }} />
            <span>{data.hours}</span>
          </div>
        </div>

        <a
          data-testid={`${testidPrefix}-book`}
          href={buildWhatsAppUrl(intent)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 btn-wa w-full sm:w-auto"
        >
          <MessageCircle className="w-4 h-4" /> {data.cta}
        </a>
      </div>
    </article>
  );
}

export default function Studios() {
  const { t } = useLang();
  const r = useReveal();
  return (
    <section id="studios" data-testid="studios-section" className="relative bg-black py-24 md:py-40">
      <div className="container-x">
        <div ref={r} className="reveal max-w-3xl">
          <p className="overline mb-4">{t.studios.overline}</p>
          <h2
            data-testid="studios-title"
            className="font-display text-4xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.95]"
          >
            {t.studios.title}
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <StudioCard
            idx={0}
            accentVar="--coral"
            image={WILLEMSTAD_IMG}
            testidPrefix="studio-curacao"
          />
          <StudioCard
            idx={1}
            accentVar="--mustard"
            image={ROTTERDAM_IMG}
            testidPrefix="studio-rotterdam"
          />
        </div>
      </div>
    </section>
  );
}
